import React, { useState, useEffect, useCallback } from 'react'
import {
  X,
  Video,
  Users,
  MapPin,
  Clock,
  Calendar,
  Send,
  Trash2,
  Loader2,
  Plus,
  UserPlus,
} from 'lucide-react'
import { useCalendarMutations } from '@/lib/data/hooks/useCalendar'
import { useAuth } from '@/lib/auth/context'
import { getNexusClient } from '@/lib/supabase/client'
import { useEmailAccounts } from '@/lib/data/hooks/useEmailAccounts'
import {
  CalendarEvent,
  CreateEventInput,
  AttendeeInput,
  EventType,
  EventColor,
  EVENT_TYPE_LABELS,
  EVENT_COLORS,
} from '@/types/calendar'

interface EventModalProps {
  isOpen: boolean
  onClose: () => void
  onSaved: () => void
  selectedSlot: { date: Date; hour: number } | null
  selectedEvent: CalendarEvent | null
}

export function EventModal({
  isOpen,
  onClose,
  onSaved,
  selectedSlot,
  selectedEvent,
}: EventModalProps) {
  const { adminUser } = useAuth()
  const { createEvent, updateEvent, deleteEvent, isLoading, error } = useCalendarMutations()

  // Get user's email accounts for "send from" dropdown
  const { accounts: emailAccounts } = useEmailAccounts({
    userId: adminUser?.id,
    activeOnly: true,
  })

  // Form state
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [location, setLocation] = useState('')
  const [startDate, setStartDate] = useState('')
  const [startTime, setStartTime] = useState('')
  const [endDate, setEndDate] = useState('')
  const [endTime, setEndTime] = useState('')
  const [hasVideoConference, setHasVideoConference] = useState(false)
  const [eventType, setEventType] = useState<EventType>('meeting')
  const [color, setColor] = useState<EventColor>('primary')
  const [sendInvites, setSendInvites] = useState(true)
  const [sendFromAccountId, setSendFromAccountId] = useState<string | null>(null)
  const [attendees, setAttendees] = useState<AttendeeInput[]>([])
  const [newAttendeeEmail, setNewAttendeeEmail] = useState('')
  const [newAttendeeName, setNewAttendeeName] = useState('')

  // Admin users for attendee selection
  const [adminUsers, setAdminUsers] = useState<{ id: string; email: string; firstName?: string; lastName?: string }[]>([])

  // Initialize form when opening
  useEffect(() => {
    if (!isOpen) return

    if (selectedEvent) {
      // Edit mode - populate from existing event
      const start = new Date(selectedEvent.startTime)
      const end = new Date(selectedEvent.endTime)

      setTitle(selectedEvent.title)
      setDescription(selectedEvent.description || '')
      setLocation(selectedEvent.location || '')
      setStartDate(start.toISOString().split('T')[0])
      setStartTime(start.toTimeString().slice(0, 5))
      setEndDate(end.toISOString().split('T')[0])
      setEndTime(end.toTimeString().slice(0, 5))
      setHasVideoConference(selectedEvent.hasVideoConference)
      setEventType(selectedEvent.eventType)
      setColor(selectedEvent.color)

      // Map attendees
      const existingAttendees: AttendeeInput[] = (selectedEvent.attendees || [])
        .filter(a => !a.isOrganizer)
        .map(a => ({
          adminUserId: a.adminUserId,
          externalEmail: a.externalEmail,
          externalName: a.externalName,
        }))
      setAttendees(existingAttendees)
    } else if (selectedSlot) {
      // Create mode - use selected slot
      const startDateTime = new Date(selectedSlot.date)
      startDateTime.setHours(selectedSlot.hour, 0, 0, 0)

      const endDateTime = new Date(startDateTime)
      endDateTime.setHours(selectedSlot.hour + 1, 0, 0, 0)

      setTitle('')
      setDescription('')
      setLocation('')
      setStartDate(startDateTime.toISOString().split('T')[0])
      setStartTime(startDateTime.toTimeString().slice(0, 5))
      setEndDate(endDateTime.toISOString().split('T')[0])
      setEndTime(endDateTime.toTimeString().slice(0, 5))
      setHasVideoConference(false)
      setEventType('meeting')
      setColor('primary')
      setAttendees([])
    }

    setNewAttendeeEmail('')
    setNewAttendeeName('')
  }, [isOpen, selectedSlot, selectedEvent])

  // Fetch admin users for attendee dropdown
  useEffect(() => {
    async function fetchAdminUsers() {
      const client = getNexusClient()
      const { data } = await client
        .from('admin_users')
        .select('id, email, first_name, last_name')
        .eq('is_active', true)
        .order('email') as { data: Array<{ id: string; email: string; first_name: string | null; last_name: string | null }> | null }

      if (data) {
        setAdminUsers(data.map(u => ({
          id: u.id,
          email: u.email,
          firstName: u.first_name || undefined,
          lastName: u.last_name || undefined,
        })))
      }
    }

    if (isOpen) {
      fetchAdminUsers()
    }
  }, [isOpen])

  // Add internal attendee
  const addInternalAttendee = useCallback((userId: string) => {
    if (!attendees.some(a => a.adminUserId === userId)) {
      setAttendees([...attendees, { adminUserId: userId }])
    }
  }, [attendees])

  // Add external attendee
  const addExternalAttendee = useCallback(() => {
    if (newAttendeeEmail && !attendees.some(a => a.externalEmail === newAttendeeEmail)) {
      setAttendees([
        ...attendees,
        { externalEmail: newAttendeeEmail, externalName: newAttendeeName || undefined },
      ])
      setNewAttendeeEmail('')
      setNewAttendeeName('')
    }
  }, [attendees, newAttendeeEmail, newAttendeeName])

  // Remove attendee
  const removeAttendee = useCallback((index: number) => {
    setAttendees(attendees.filter((_, i) => i !== index))
  }, [attendees])

  // Handle save
  const handleSave = useCallback(async () => {
    if (!title.trim()) return

    const startDateTime = new Date(`${startDate}T${startTime}`)
    const endDateTime = new Date(`${endDate}T${endTime}`)

    if (endDateTime <= startDateTime) {
      alert('End time must be after start time')
      return
    }

    const input: CreateEventInput = {
      title: title.trim(),
      description: description.trim() || undefined,
      location: location.trim() || undefined,
      startTime: startDateTime.toISOString(),
      endTime: endDateTime.toISOString(),
      hasVideoConference,
      eventType,
      color,
      attendees: attendees.length > 0 ? attendees : undefined,
      sendInvites: sendInvites && attendees.length > 0,
      sendFromAccountId: sendFromAccountId || undefined,
    }

    let success = false

    if (selectedEvent) {
      const result = await updateEvent({ id: selectedEvent.id, ...input })
      success = !!result
    } else {
      const result = await createEvent(input)
      success = !!result
    }

    if (success) {
      onSaved()
    }
  }, [
    title,
    description,
    location,
    startDate,
    startTime,
    endDate,
    endTime,
    hasVideoConference,
    eventType,
    color,
    attendees,
    sendInvites,
    sendFromAccountId,
    selectedEvent,
    createEvent,
    updateEvent,
    onSaved,
  ])

  // Handle delete
  const handleDelete = useCallback(async () => {
    if (!selectedEvent) return

    if (!confirm('Are you sure you want to delete this event?')) return

    const success = await deleteEvent(selectedEvent.id)
    if (success) {
      onSaved()
    }
  }, [selectedEvent, deleteEvent, onSaved])

  const isEditing = !!selectedEvent

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-black/50 backdrop-blur-sm transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Slide-out Panel */}
      <div
        className={`fixed top-0 right-0 z-50 h-full w-full max-w-xl bg-[var(--bg-card)] border-l border-[var(--border)] shadow-2xl flex flex-col transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-[var(--border)]">
          <div>
            <h3 className="text-xl font-semibold text-[var(--text-main)]">
              {isEditing ? 'Edit Event' : 'New Event'}
            </h3>
            <p className="text-sm text-[var(--text-muted)] mt-1">
              {isEditing ? 'Update event details' : 'Create a new calendar event'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-element)] rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-main)] mb-2">
              Event Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter event title"
              className="w-full bg-[var(--bg-main)] border border-[var(--border)] rounded-lg px-4 py-3 text-[var(--text-main)] focus:outline-none focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20 placeholder-[var(--text-muted)] transition-all"
              autoFocus
            />
          </div>

          {/* Date & Time */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-[var(--text-main)] mb-2">
                Start Date & Time
              </label>
              <div className="space-y-2">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full bg-[var(--bg-main)] border border-[var(--border)] rounded-lg px-4 py-3 text-[var(--text-main)] focus:outline-none focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20 transition-all"
                />
                <input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="w-full bg-[var(--bg-main)] border border-[var(--border)] rounded-lg px-4 py-3 text-[var(--text-main)] focus:outline-none focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20 transition-all"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-main)] mb-2">
                End Date & Time
              </label>
              <div className="space-y-2">
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full bg-[var(--bg-main)] border border-[var(--border)] rounded-lg px-4 py-3 text-[var(--text-main)] focus:outline-none focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20 transition-all"
                />
                <input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="w-full bg-[var(--bg-main)] border border-[var(--border)] rounded-lg px-4 py-3 text-[var(--text-main)] focus:outline-none focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20 transition-all"
                />
              </div>
            </div>
          </div>

          {/* Event Type & Color */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-[var(--text-main)] mb-2">
                Event Type
              </label>
              <select
                value={eventType}
                onChange={(e) => setEventType(e.target.value as EventType)}
                className="w-full bg-[var(--bg-main)] border border-[var(--border)] rounded-lg px-4 py-3 text-[var(--text-main)] focus:outline-none focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20 transition-all"
              >
                {Object.entries(EVENT_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-main)] mb-2">
                Color
              </label>
              <div className="flex gap-3 flex-wrap p-3 bg-[var(--bg-main)] border border-[var(--border)] rounded-lg">
                {(Object.keys(EVENT_COLORS) as EventColor[]).map((c) => (
                  <button
                    key={c}
                    onClick={() => setColor(c)}
                    className={`w-8 h-8 rounded-full border-2 transition-all ${
                      color === c
                        ? 'border-white scale-110 ring-2 ring-white/30'
                        : 'border-transparent hover:scale-105'
                    }`}
                    style={{ backgroundColor: EVENT_COLORS[c].border }}
                    title={c}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Video Conference Toggle */}
          <div className="space-y-3">
            <div className="flex items-center justify-between p-4 bg-[var(--bg-element)] rounded-xl border border-[var(--border)]">
              <div className="flex items-center gap-4">
                <div
                  className={`p-3 rounded-xl ${
                    hasVideoConference
                      ? 'bg-indigo-500/20 text-indigo-400'
                      : 'bg-[var(--bg-card)] text-[var(--text-muted)]'
                  }`}
                >
                  <Video size={22} />
                </div>
                <div>
                  <p className="font-medium text-[var(--text-main)]">Video Conference</p>
                  <p className="text-sm text-[var(--text-muted)]">
                    {hasVideoConference ? 'Meeting room will be created' : 'Add video meeting to this event'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setHasVideoConference(!hasVideoConference)}
                className={`w-14 h-7 rounded-full relative transition-colors ${
                  hasVideoConference
                    ? 'bg-[var(--primary)]'
                    : 'bg-[var(--bg-card)] border border-[var(--border)]'
                }`}
              >
                <div
                  className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow-sm transition-transform ${
                    hasVideoConference ? 'left-[30px]' : 'left-0.5'
                  }`}
                />
              </button>
            </div>

            {/* Meeting Link - show when editing an event with video conference */}
            {selectedEvent?.meetingRoomLink && (
              <div className="p-4 bg-indigo-500/10 border border-indigo-500/20 rounded-xl">
                <p className="text-sm font-medium text-indigo-400 mb-2">Meeting Link</p>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={selectedEvent.meetingRoomLink}
                    className="flex-1 bg-[var(--bg-main)] border border-[var(--border)] rounded-lg px-3 py-2 text-sm text-[var(--text-main)] focus:outline-none"
                  />
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(selectedEvent.meetingRoomLink!)
                    }}
                    className="px-3 py-2 bg-indigo-500 text-white text-sm font-medium rounded-lg hover:bg-indigo-600 transition-colors"
                  >
                    Copy
                  </button>
                  <a
                    href={selectedEvent.meetingRoomLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-2 bg-[var(--primary)] text-white text-sm font-medium rounded-lg hover:opacity-90 transition-opacity"
                  >
                    Join
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-main)] mb-2">
              <MapPin size={14} className="inline mr-2" />Location
              <span className="text-[var(--text-muted)] font-normal ml-1">(Optional)</span>
            </label>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Add a location or address"
              className="w-full bg-[var(--bg-main)] border border-[var(--border)] rounded-lg px-4 py-3 text-[var(--text-main)] focus:outline-none focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20 placeholder-[var(--text-muted)] transition-all"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-main)] mb-2">
              Description
              <span className="text-[var(--text-muted)] font-normal ml-1">(Optional)</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add a description or notes for this event"
              rows={4}
              className="w-full bg-[var(--bg-main)] border border-[var(--border)] rounded-lg px-4 py-3 text-[var(--text-main)] focus:outline-none focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20 placeholder-[var(--text-muted)] resize-none transition-all"
            />
          </div>

          {/* Attendees */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-main)] mb-3">
              <Users size={14} className="inline mr-2" />Attendees
            </label>

            {/* Current Attendees */}
            {attendees.length > 0 && (
              <div className="space-y-2 mb-4">
                {attendees.map((attendee, index) => {
                  const user = attendee.adminUserId
                    ? adminUsers.find(u => u.id === attendee.adminUserId)
                    : null

                  return (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-[var(--bg-element)] rounded-lg border border-[var(--border)]"
                    >
                      <span className="text-[var(--text-main)]">
                        {user
                          ? `${user.firstName || ''} ${user.lastName || ''} (${user.email})`.trim()
                          : `${attendee.externalName || ''} <${attendee.externalEmail}>`.trim()}
                      </span>
                      <button
                        onClick={() => removeAttendee(index)}
                        className="p-1 text-[var(--text-muted)] hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                      >
                        <X size={18} />
                      </button>
                    </div>
                  )
                })}
              </div>
            )}

            {/* Add Internal Attendee */}
            <select
              value=""
              onChange={(e) => {
                if (e.target.value) {
                  addInternalAttendee(e.target.value)
                }
              }}
              className="w-full bg-[var(--bg-main)] border border-[var(--border)] rounded-lg px-4 py-3 text-[var(--text-main)] focus:outline-none focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20 mb-3 transition-all"
            >
              <option value="">Add team member...</option>
              {adminUsers
                .filter(u => u.id !== adminUser?.id && !attendees.some(a => a.adminUserId === u.id))
                .map(user => (
                  <option key={user.id} value={user.id}>
                    {user.firstName} {user.lastName} ({user.email})
                  </option>
                ))}
            </select>

            {/* Add External Attendee */}
            <div className="flex gap-3">
              <input
                type="email"
                value={newAttendeeEmail}
                onChange={(e) => setNewAttendeeEmail(e.target.value)}
                placeholder="External email address"
                className="flex-1 bg-[var(--bg-main)] border border-[var(--border)] rounded-lg px-4 py-3 text-[var(--text-main)] focus:outline-none focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20 placeholder-[var(--text-muted)] transition-all"
              />
              <input
                type="text"
                value={newAttendeeName}
                onChange={(e) => setNewAttendeeName(e.target.value)}
                placeholder="Name"
                className="w-36 bg-[var(--bg-main)] border border-[var(--border)] rounded-lg px-4 py-3 text-[var(--text-main)] focus:outline-none focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20 placeholder-[var(--text-muted)] transition-all"
              />
              <button
                onClick={addExternalAttendee}
                disabled={!newAttendeeEmail}
                className="px-4 py-3 bg-[var(--bg-element)] border border-[var(--border)] rounded-lg text-[var(--text-main)] hover:bg-[var(--bg-hover)] transition-colors disabled:opacity-50"
              >
                <UserPlus size={18} />
              </button>
            </div>

            {/* Send Invites Toggle */}
            {attendees.length > 0 && (
              <div className="mt-4 space-y-3">
                <label className="flex items-center gap-3 p-3 bg-[var(--bg-element)] rounded-lg border border-[var(--border)] cursor-pointer hover:bg-[var(--bg-hover)] transition-colors">
                  <input
                    type="checkbox"
                    checked={sendInvites}
                    onChange={(e) => setSendInvites(e.target.checked)}
                    className="w-4 h-4 rounded border-[var(--border)]"
                  />
                  <Send size={16} className="text-[var(--text-muted)]" />
                  <span className="text-[var(--text-main)]">Send email invites to attendees</span>
                </label>

                {/* Email Account Selector - only show when sending invites and have multiple accounts */}
                {sendInvites && emailAccounts.length > 1 && (
                  <div className="pl-10">
                    <label className="block text-sm text-[var(--text-muted)] mb-2">
                      Send from:
                    </label>
                    <select
                      value={sendFromAccountId || emailAccounts[0]?.id || ''}
                      onChange={(e) => setSendFromAccountId(e.target.value)}
                      className="w-full bg-[var(--bg-main)] border border-[var(--border)] rounded-lg px-4 py-2.5 text-sm text-[var(--text-main)] focus:outline-none focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20 transition-all"
                    >
                      {emailAccounts.map(account => (
                        <option key={account.id} value={account.id}>
                          {account.emailAddress}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-sm text-red-400">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-[var(--border)] bg-[var(--bg-element)]">
          <div className="flex justify-between items-center">
            <div>
              {isEditing && (
                <button
                  onClick={handleDelete}
                  disabled={isLoading}
                  className="flex items-center gap-2 px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50"
                >
                  <Trash2 size={16} /> Delete Event
                </button>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-5 py-2.5 text-sm text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-hover)] rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isLoading || !title.trim()}
                className="flex items-center gap-2 px-6 py-2.5 bg-[var(--primary)] text-white text-sm font-medium rounded-lg hover:opacity-90 shadow-lg shadow-[var(--primary)]/20 disabled:opacity-50 transition-all"
              >
                {isLoading && <Loader2 size={16} className="animate-spin" />}
                {isEditing ? 'Update Event' : 'Create Event'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default EventModal
